/**
 * Storage core — bucket constants, URL resolution, and file uploads.
 * Pure logic with injected SupabaseClient; no client creation here.
 * Shared by the browser (src/lib/services/storage.ts) and the API (api/ routes).
 *
 * Stored values use bucket-prefixed paths: "bucket-name/path/to/file".
 * resolveUrl is the single resolver - callers never reference bucket names.
 */

import type { SupabaseClient } from '@supabase/supabase-js'

const SIGNED_URL_TTL = 3600

const BUCKETS = {
  EMPLOYEES:        'employees',
  INCIDENTS:        'incidents',
  ENCOUNTERS:       'encounters',
  UNITS:            'units',
  ITEM_CATALOG:     'item-catalog',
  CHAT:             'chat',
  EXTERNAL_ACCESS:  'external-access',
  DOCUMENTS:        'documents',
  BRANDING:         'branding',
  SIGNATURES:       'signatures',
} as const

const PUBLIC_BUCKETS = new Set<string>([BUCKETS.BRANDING])

/** All bucket names the resolver recognizes, including legacy names. */
const KNOWN_BUCKETS = new Set<string>([
  ...Object.values(BUCKETS),
  'headshots',
  'patient-photos',
  'credentials',
  'chat-files',
])

// ── Resolution ───────────────────────────────────────────────────────────────

/** Resolves a stored DB value to a usable URL.
 *  Stored values are bucket/path.
 *  - undefined / empty → undefined
 *  - data: URIs → passthrough
 *  - external http/https URLs → passthrough
 *  - bucket/path → public URL or signed URL depending on bucket type
 */
export async function resolveUrl(
  supabase: SupabaseClient,
  stored: string | undefined,
  expiresIn = SIGNED_URL_TTL
): Promise<string | undefined> {
  if (!stored) return undefined
  if (stored.startsWith('data:')) return stored
  if (stored.startsWith('http://') || stored.startsWith('https://')) return stored

  const slash = stored.indexOf('/')
  if (slash < 0) return undefined
  const bucket = stored.slice(0, slash)
  if (!KNOWN_BUCKETS.has(bucket)) return undefined
  const path = stored.slice(slash + 1)

  if (PUBLIC_BUCKETS.has(bucket)) {
    return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl
  }

  const { data } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn)
  return data?.signedUrl ?? undefined
}

/** Batch-resolve stored DB values to usable URLs.
 *  Groups paths by bucket and calls createSignedUrls once per bucket.
 *  Returns a Map from stored value to resolved URL.
 */
export async function resolveUrls(
  supabase: SupabaseClient,
  stored: (string | undefined)[],
  expiresIn = SIGNED_URL_TTL
): Promise<Map<string, string>> {
  const result = new Map<string, string>()
  const bucketGroups = new Map<string, { stored: string; path: string }[]>()

  for (const s of stored) {
    if (!s || result.has(s)) continue
    if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://')) {
      result.set(s, s)
      continue
    }
    const slash = s.indexOf('/')
    if (slash < 0) continue
    const bucket = s.slice(0, slash)
    if (!KNOWN_BUCKETS.has(bucket)) continue
    const path = s.slice(slash + 1)

    if (PUBLIC_BUCKETS.has(bucket)) {
      result.set(s, supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl)
      continue
    }

    let group = bucketGroups.get(bucket)
    if (!group) { group = []; bucketGroups.set(bucket, group) }
    group.push({ stored: s, path })
  }

  await Promise.all(
    Array.from(bucketGroups.entries()).map(async ([bucket, items]) => {
      const { data } = await supabase.storage
        .from(bucket)
        .createSignedUrls(items.map(i => i.path), expiresIn)
      if (data) {
        for (let i = 0; i < data.length; i++) {
          const signed = data[i].signedUrl
          if (signed) result.set(items[i].stored, signed)
        }
      }
    })
  )

  return result
}

// ── Uploads ──────────────────────────────────────────────────────────────────

/** Upload a file and return `bucket/path` as the stored value. */
async function upload(
  supabase: SupabaseClient,
  bucket: string,
  path: string,
  data: Blob | ArrayBufferView,
  contentType: string,
  options?: { upsert?: boolean }
): Promise<string> {
  const { error } = await supabase.storage.from(bucket).upload(path, data, { contentType, upsert: options?.upsert ?? false })
  if (error) throw error
  return `${bucket}/${path}`
}

export async function uploadAmaPdf(supabase: SupabaseClient, encounterUuid: string | null, consentId: string, data: Blob | ArrayBufferView): Promise<string> {
  if (encounterUuid) return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/ama-forms/${consentId}`, data, 'application/pdf', { upsert: true })
  return upload(supabase, BUCKETS.DOCUMENTS, `ama/${consentId}`, data, 'application/pdf', { upsert: true })
}

export async function uploadCatalogImage(supabase: SupabaseClient, catalogItemId: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.ITEM_CATALOG, `${catalogItemId}/photo`, data, contentType, { upsert: true })
}

export async function uploadChatAttachment(supabase: SupabaseClient, channelId: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.CHAT, `${channelId}/uploads/${crypto.randomUUID()}`, data, contentType)
}

export async function uploadCompClaimPdf(supabase: SupabaseClient, encounterUuid: string | null, claimId: string, data: Blob | ArrayBufferView): Promise<string> {
  if (encounterUuid) return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/comp-claims/${claimId}`, data, 'application/pdf', { upsert: true })
  return upload(supabase, BUCKETS.DOCUMENTS, `comp-claims/${claimId}`, data, 'application/pdf', { upsert: true })
}

export async function uploadConsentSignature(supabase: SupabaseClient, incidentId: string | null, consentId: string, data: Blob | ArrayBufferView, contentType = 'image/png'): Promise<string> {
  const folder = incidentId ? `consent/${incidentId}` : 'unlinked'
  return upload(supabase, BUCKETS.SIGNATURES, `${folder}/${consentId}`, data, contentType)
}

export async function uploadAMASignature(supabase: SupabaseClient, incidentId: string | null, consentId: string, role: 'patient' | 'provider', data: Blob | ArrayBufferView, contentType = 'image/png'): Promise<string> {
  const folder = incidentId ? `ama/${incidentId}` : 'unlinked'
  return upload(supabase, BUCKETS.SIGNATURES, `${folder}/${consentId}/${role}`, data, contentType)
}

export async function uploadConsentToTreatPdf(supabase: SupabaseClient, encounterUuid: string | null, consentId: string, data: Blob | ArrayBufferView): Promise<string> {
  if (encounterUuid) return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/consent-forms/${consentId}`, data, 'application/pdf', { upsert: true })
  return upload(supabase, BUCKETS.DOCUMENTS, `consent-to-treat/${consentId}`, data, 'application/pdf', { upsert: true })
}

export async function uploadCredential(supabase: SupabaseClient, employeeId: string, canonicalName: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.EMPLOYEES, `${employeeId}/credentials/${canonicalName}`, data, contentType, { upsert: true })
}

export async function uploadEmployeeHeadshot(supabase: SupabaseClient, employeeId: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.EMPLOYEES, `${employeeId}/photo`, data, contentType, { upsert: true })
}

export async function uploadEncounterPhoto(supabase: SupabaseClient, encounterUuid: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/photos/${crypto.randomUUID()}`, data, contentType)
}

export async function uploadExpenseReceipt(supabase: SupabaseClient, incidentId: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.INCIDENTS, `${incidentId}/expenses/${crypto.randomUUID()}`, data, contentType)
}

export async function uploadExternalAvatar(supabase: SupabaseClient, accessCodeId: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.EXTERNAL_ACCESS, `${accessCodeId}/avatar`, data, contentType, { upsert: true })
}

export async function uploadICS214Pdf(supabase: SupabaseClient, incidentId: string, ics214Id: string, data: Blob | ArrayBufferView): Promise<string> {
  return upload(supabase, BUCKETS.INCIDENTS, `${incidentId}/ics214/${ics214Id}`, data, 'application/pdf', { upsert: true })
}

export async function uploadICS214Signature(supabase: SupabaseClient, incidentId: string, ics214Id: string, data: Blob | ArrayBufferView, contentType = 'image/png'): Promise<string> {
  return upload(supabase, BUCKETS.SIGNATURES, `ics214/${incidentId}/${ics214Id}`, data, contentType, { upsert: true })
}

export async function uploadIncidentContract(supabase: SupabaseClient, incidentId: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.INCIDENTS, `${incidentId}/contract`, data, contentType, { upsert: true })
}

export async function uploadNemsisXml(supabase: SupabaseClient, encounterUuid: string, data: Blob | ArrayBufferView): Promise<string> {
  return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/nemsis-exports/${crypto.randomUUID()}`, data, 'application/xml')
}

export async function uploadOrgBackground(supabase: SupabaseClient, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.BRANDING, 'background', data, contentType, { upsert: true })
}

export async function uploadOrgDocument(supabase: SupabaseClient, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.DOCUMENTS, crypto.randomUUID(), data, contentType)
}

export async function uploadOrgLogo(supabase: SupabaseClient, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.BRANDING, 'logo', data, contentType, { upsert: true })
}

export async function uploadUnitPhoto(supabase: SupabaseClient, unitId: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.UNITS, `${unitId}/photo`, data, contentType, { upsert: true })
}

export async function uploadVehicleDocument(supabase: SupabaseClient, unitId: string, data: Blob | ArrayBufferView, contentType: string): Promise<string> {
  return upload(supabase, BUCKETS.UNITS, `${unitId}/documents/${crypto.randomUUID()}`, data, contentType)
}
